/**
 * ClaimCTA — Iter V2 Certificate Claim.
 *
 * Premium claim button used above the certificate image AND as the final
 * page CTA. Renders the primary "CLAIM MY PROFILE" action plus an
 * unobtrusive existing-user "SIGN IN & CLAIM" secondary link.
 *
 * Preserves the claim context through auth by threading the certificate
 * number into every navigation target:
 *   * Logged-out new user  → /register?claim={cert_number}
 *   * Logged-out existing  → /login?claim={cert_number}
 *   * Logged-in collector  → /dashboard/claim?cert={cert_number}
 *
 * The button intentionally does NOT look like a Bootstrap primary — heavy
 * type, wide padding, rounded, with clear hover/pressed/focus states.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useCollectorAuth } from "@/context/CollectorAuthContext";

export default function ClaimCTA({ certNumber, testidPrefix = "cta", subtle = false }) {
  const { collector } = useCollectorAuth();
  const primary = collector
    ? `/dashboard/claim?cert=${encodeURIComponent(certNumber)}`
    : `/register?claim=${encodeURIComponent(certNumber)}`;
  const signInHref = `/login?claim=${encodeURIComponent(certNumber)}`;

  return (
    <div className={"w-full " + (subtle ? "" : "text-center")}>
      <Link
        to={primary}
        data-testid={`${testidPrefix}-primary`}
        className={
          "group relative inline-flex w-full items-center justify-center " +
          "rounded-2xl bg-[var(--panini-red)] hover:bg-[var(--panini-red-deep)] " +
          "active:translate-y-[1px] focus-visible:outline-none " +
          "focus-visible:ring-4 focus-visible:ring-[var(--collector-gold)]/60 " +
          "text-white font-display uppercase tracking-wide " +
          "text-lg md:text-xl px-8 py-5 md:py-6 " +
          "border-2 border-[var(--ink)] shadow-[6px_6px_0_var(--ink)] " +
          "transition-[transform,box-shadow,background-color] duration-150 " +
          "hover:shadow-[4px_4px_0_var(--ink)]"
        }
      >
        Claim My Profile
      </Link>
      <p className="mt-3 text-xs uppercase tracking-widest text-white/70">
        Free · Secure · Takes less than 60 seconds
      </p>
      {!collector && (
        <p className="mt-4 text-sm text-white/85">
          Already have a CollectorUp account?{" "}
          <Link
            to={signInHref}
            data-testid={`${testidPrefix}-signin`}
            className="font-bold text-[var(--collector-gold-soft)] underline underline-offset-4 hover:text-white"
          >
            Sign in &amp; claim
          </Link>
        </p>
      )}
    </div>
  );
}
